import { sanitizeNarration } from './narrationSanitizer';

// Keep the first request deliberately small enough to reduce synthesis latency;
// the same ceiling keeps subsequent chunks natural and predictable.
const DEFAULT_MAX_CHARS = 180;
const DEFAULT_PREFETCH = 2;

export function narrationCompletionOwnsUi(result, replacementActive) {
    return Boolean(result && !result.cancelled && !replacementActive);
}

export function shouldShowAutoplayFallback(result) {
    return Boolean(result && !result.cancelled && !result.started);
}

function sentenceUnits(paragraph) {
    const units = [];
    let start = 0;
    for (let index = 0; index < paragraph.length; index += 1) {
        if (!'.!?'.includes(paragraph[index])) continue;
        let end = index + 1;
        while (end < paragraph.length && '.!?'.includes(paragraph[end])) end += 1;
        if (end < paragraph.length && '”’"\''.includes(paragraph[end])) end += 1;
        if (end < paragraph.length && !/\s/.test(paragraph[end])) continue;
        const sentence = paragraph.slice(start, end).trim();
        if (sentence) units.push(sentence);
        while (end < paragraph.length && /\s/.test(paragraph[end])) end += 1;
        start = end;
        index = end - 1;
    }
    const tail = paragraph.slice(start).trim();
    if (tail) units.push(tail);
    return units;
}

export function splitNarration(text, maxChars = DEFAULT_MAX_CHARS) {
    const paragraphs = String(text || '').split(/\n+/).map(value => value.trim()).filter(Boolean);
    const chunks = [];
    const pushBounded = value => {
        let remaining = value.trim();
        while (remaining.length > maxChars) {
            const window = remaining.slice(0, maxChars + 1);
            const sentence = Math.max(window.lastIndexOf('. '), window.lastIndexOf('? '), window.lastIndexOf('! '), window.lastIndexOf('; '));
            const comma = window.lastIndexOf(', ');
            const space = window.lastIndexOf(' ');
            const cut = sentence >= Math.floor(maxChars * 0.45)
                ? sentence + 1
                : comma >= Math.floor(maxChars * 0.6) ? comma + 1 : space > 0 ? space : maxChars;
            chunks.push(remaining.slice(0, cut).trim());
            remaining = remaining.slice(cut).trim();
        }
        if (remaining) chunks.push(remaining);
    };

    paragraphs.forEach(paragraph => {
        const sentences = sentenceUnits(paragraph);
        let current = '';
        sentences.forEach(sentence => {
            const joined = current ? `${current} ${sentence}` : sentence;
            if (current && joined.length > maxChars) {
                pushBounded(current);
                current = sentence;
            } else {
                current = joined;
            }
        });
        if (current) pushBounded(current);
    });
    return chunks;
}

export function createNarrationQueue({ tts, sanitize = sanitizeNarration, prefetch = DEFAULT_PREFETCH, now = () => performance.now(), onTiming = console.info } = {}) {
    let active = null;
    let sequence = 0;

    const emit = (session, event, details = {}) => {
        const payload = {
            event,
            voice: session.voice || null,
            utteranceId: session.id,
            elapsedMs: Math.round((now() - session.startedAt) * 100) / 100,
            ...details,
        };
        onTiming?.('[HelpVoiceTiming]', JSON.stringify(payload));
    };

    const cancel = (reason = 'cancelled') => {
        const session = active;
        if (!session || session.cancelled) return;
        session.cancelled = true;
        session.cancelReason = reason;
        session.controllers.forEach(controller => controller.abort());
        session.prepared.forEach(item => { if (item) tts.dispose(item); });
        tts.stop();
        emit(session, 'cancel', { reason });
        if (active === session) active = null;
    };

    const release = session => {
        session.closed = true;
        session.controllers.forEach(controller => controller.abort());
        session.prepared.forEach(item => { if (item) tts.dispose(item); });
    };

    const start = async (text, options = {}) => {
        cancel('superseded');
        const session = {
            id: ++sequence,
            voice: options.voice || null,
            startedAt: options.renderedAt ?? now(),
            cancelled: false,
            closed: false,
            controllers: [],
            prepared: [],
            requests: [],
            firstPlayableReady: false,
        };
        active = session;
        emit(session, 'response_rendered', { elapsedMs: 0 });
        const narration = sanitize(text);
        options.onSanitized?.(narration);
        const chunks = splitNarration(narration, options.maxChars);
        const voice = options.voice;
        emit(session, 'voice_resolved', { voice, chunkCount: chunks.length });
        if (!chunks.length) {
            emit(session, 'completion', { playedChunks: 0 });
            active = null;
            return { started: false, completed: true, narration, chunks };
        }

        const requestChunk = index => {
            if (session.cancelled || index >= chunks.length || session.requests[index]) return session.requests[index];
            const controller = new AbortController();
            session.controllers[index] = controller;
            if (index === 0) emit(session, 'first_request', { chunk: index });
            emit(session, 'chunk_fetch_start', { chunk: index });
            session.requests[index] = Promise.resolve(tts.prepare(chunks[index], {
                voice,
                context: options.context,
                signal: controller.signal,
            })).then(prepared => {
                if (session.cancelled || session.closed) {
                    if (prepared) tts.dispose(prepared);
                    return null;
                }
                if (!prepared) {
                    emit(session, 'error', { phase: 'fetch', chunk: index, message: 'No audio returned' });
                    return null;
                }
                session.prepared[index] = prepared;
                emit(session, 'chunk_fetch_ready', { chunk: index });
                return prepared;
            }).catch(error => {
                if (!session.cancelled && error?.name !== 'AbortError') emit(session, 'error', { phase: 'fetch', chunk: index, message: error?.message || String(error) });
                return null;
            });
            return session.requests[index];
        };

        for (let index = 0; index < Math.min(prefetch, chunks.length); index += 1) requestChunk(index);
        let started = false;
        let playedChunks = 0;
        for (let index = 0; index < chunks.length; index += 1) {
            if (session.cancelled || active !== session) return { started, completed: false, cancelled: true, cancelReason: session.cancelReason, narration, chunks };
            const prepared = await requestChunk(index);
            if (session.cancelled || active !== session) return { started, completed: false, cancelled: true, cancelReason: session.cancelReason, narration, chunks };
            if (!prepared) {
                requestChunk(index + prefetch);
                continue;
            }
            if (!session.firstPlayableReady) {
                session.firstPlayableReady = true;
                emit(session, 'first_audio_ready', { chunk: index });
            }
            if (!started) emit(session, 'first_audio_start', { chunk: index });
            emit(session, 'chunk_play_start', { chunk: index });
            let played = false;
            let playError = null;
            try {
                played = await tts.play(prepared, { volume: options.volume });
            } catch (error) {
                playError = error;
            }
            if (session.cancelled || active !== session) return { started, completed: false, cancelled: true, cancelReason: session.cancelReason, narration, chunks };
            if (!played) {
                emit(session, 'error', { phase: 'play', chunk: index, ...(playError ? { message: playError.message || String(playError) } : {}) });
                break;
            }
            started = true;
            playedChunks += 1;
            emit(session, 'chunk_play_end', { chunk: index });
            // The completed/disposed current chunk releases one strict window
            // slot before another prefetch begins.
            requestChunk(index + prefetch);
        }
        release(session);
        if (active === session) active = null;
        emit(session, 'completion', { playedChunks });
        return { started, completed: true, narration, chunks };
    };

    return { start, cancel, isActive: () => Boolean(active) };
}
